import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AwardsService } from '../services';
import { MEMBERSHIP_STATUS_LABELS } from '../statuses';
import { awardsKeys } from '../queryKeys';

/**
 * Optimistically repaints the row, then rolls back if the server disagrees.
 * The select only offers legal transitions, so a rejection here means the row
 * moved underneath us — refetch to resync rather than trusting local state.
 */
export const useUpdateMembershipStatus = (cycleId, options = {}) => {
  const { onError } = options;
  const queryClient = useQueryClient();

  return useMutation({
    meta: { skipGlobalErrorToaster: true },
    mutationFn: ({ userId, status }) =>
      AwardsService.updateMembershipStatus(cycleId, userId, status),
    onMutate: async ({ userId, status }) => {
      const filter = { queryKey: awardsKeys.membershipsPrefix(cycleId) };
      await queryClient.cancelQueries(filter);

      const snapshots = queryClient.getQueriesData(filter);

      snapshots.forEach(([key, data]) => {
        if (!data?.items) return;

        queryClient.setQueryData(key, {
          ...data,
          items: data.items.map((row) =>
            row.userId === userId
              ? {
                ...row,
                status,
                statusLabel: MEMBERSHIP_STATUS_LABELS[status] ?? status,
              }
              : row,
          ),
        });
      });

      return { snapshots };
    },
    onError: (error, _variables, context) => {
      context?.snapshots?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      onError?.(error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: awardsKeys.membershipsPrefix(cycleId),
      });
    },
  });
};
